import { useEffect, useState } from 'react';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { CHAIN_IDS_SUPPORT_GASLESS, SUPPORTED_CHAINS } from '@/constants/chain';

import { ChainAddress } from '@/utils/models';

type SlippagePreset = 0.001 | 0.0025 | 0.005 | 0.0075 | 0.01; // Percentages

export const SLIPPAGE_PRESETS: SlippagePreset[] = [0.001, 0.0025, 0.005, 0.0075, 0.01];
export const DEFAULT_SLIPPAGE_PERCENT = 0.005;
export const DEFAULT_DYNAMIC_SLIPPAGE = true;
export const SLIPPAGE_BPS_MULTIPLIER = 10_000;
const CURRENT_SETTINGS_VERSION = 1;

interface MarketTradeSettings {
  slippage: number;
  isSlippageCustom: boolean;
  dynamicSlippage: boolean;
}

interface MarketTradeSettingsStore {
  isGasless: boolean;
  /** The current trade settings, divided by chain id */
  settings: Record<number, MarketTradeSettings>;
  /** The recipient address for swap-and-send (undefined means send to self) */
  recipientAddress: ChainAddress | undefined;
  /** Whether the user has altered the settings on a chain id  */
  hasAlteredSettings: (chainId: number) => boolean;
  /** Resets the settings to their default values for chain id */
  resetSettings: (chainId: number) => void;
  /** Sets the trade settings for chain id */
  setSettings: (chainId: number, settings: Partial<MarketTradeSettings>) => void;
  /** Sets whether the trade is gasless on a chain id */
  setIsGasless: (isGasless: boolean) => void;
  /** Sets the recipient address for swap-and-send */
  setRecipientAddress: (address: ChainAddress | undefined) => void;
}

const INITIAL_SETTINGS: MarketTradeSettings = {
  slippage: DEFAULT_SLIPPAGE_PERCENT,
  isSlippageCustom: false,
  dynamicSlippage: DEFAULT_DYNAMIC_SLIPPAGE,
};

const initialSettingsForChainId = (chainId: number) => ({
  ...INITIAL_SETTINGS,
  isGasless: CHAIN_IDS_SUPPORT_GASLESS.includes(chainId),
});

// store
const useInternalMarketTradeSettingsStore = createWithEqualityFn<MarketTradeSettingsStore>()(
  persist(
    (set, get) => ({
      isGasless: true,
      // state
      settings: Object.fromEntries(
        SUPPORTED_CHAINS.map((chain) => chain.id).map((id) => [id, initialSettingsForChainId(id)]),
      ),
      recipientAddress: undefined,
      // methods
      hasAlteredSettings: (chainId: number) => {
        const currentSettings = get().settings[chainId] ?? {};
        return (
          !get().isGasless ||
          // need to test each property for undefined because of the way INITIAL_SETTINGS is typed
          currentSettings.slippage !== INITIAL_SETTINGS.slippage ||
          currentSettings.isSlippageCustom !== INITIAL_SETTINGS.isSlippageCustom ||
          currentSettings.dynamicSlippage !== INITIAL_SETTINGS.dynamicSlippage
        );
      },

      resetSettings: (chainId: number) =>
        set((state) => {
          const newSettings = { ...state.settings };
          newSettings[chainId] = initialSettingsForChainId(chainId);
          return { settings: newSettings, isGasless: state.isGasless };
        }),

      setSettings: (chainId: number, settings: Partial<MarketTradeSettings>) =>
        set((state) => {
          const newSettings = { ...state.settings };
          newSettings[chainId] = { ...newSettings[chainId], ...settings };
          return { settings: newSettings, isGasless: state.isGasless };
        }),

      setIsGasless: (isGasless: boolean) =>
        set((state) => {
          return { isGasless, settings: state.settings };
        }),

      setRecipientAddress: (address: ChainAddress | undefined) =>
        set(() => {
          return { recipientAddress: address };
        }),
    }),
    {
      name: 'matcha-market-trade-settings-storage',
      // to prevent zustand to automatically rehydrate
      skipHydration: true,
      version: CURRENT_SETTINGS_VERSION,
      partialize: (state) => ({
        isGasless: state.isGasless,
        settings: state.settings,
      }),
      migrate: (persistedState, version) => {
        // if we need to migrate from old settings to new settings, we can do it here

        // MAT-4071, make the Matcha Auto toggle global
        if (version === 0) {
          const typed = persistedState as {
            settings: { [key: number]: MarketTradeSettings & { isGasless?: boolean } };
          };
          if (typed.settings && typeof typed.settings === 'object') {
            let isGasless = true;
            for (const setting of Object.values(typed.settings)) {
              const { isGasless: persistedIsGasless } = setting;
              delete setting.isGasless;
              isGasless = isGasless && !!persistedIsGasless;
            }
            (persistedState as MarketTradeSettingsStore).isGasless = isGasless;
          }
        }

        return persistedState as MarketTradeSettingsStore;
      },
    },
  ),
  shallow,
);

/**
 * This function is a wrapper for the standard exported hook provided by zustand.
 * The hook ensures that the zustand store is only hydrated after the initial render cycle, to
 * prevent hydration mismatches
 */
export function useMarketTradeSettingsStore<T>(
  selector: (state: MarketTradeSettingsStore) => T = (x) => x as T,
) {
  const [rehydrated, setIsRehydrated] = useState(false);

  useEffect(() => {
    if (!rehydrated) {
      useInternalMarketTradeSettingsStore.persist.rehydrate();
      setIsRehydrated(true);
    }
  }, [rehydrated]);

  return useInternalMarketTradeSettingsStore(selector);
}

// Selectors
export const resetSettingsSelector = (chainId: number) => (state: MarketTradeSettingsStore) => () =>
  state.resetSettings(chainId);

export const gaslessSelectors = (state: MarketTradeSettingsStore) => {
  return {
    isGasless: state.isGasless,
    setIsGasless: state.setIsGasless,
  };
};

export const slippageSelector = (chainId: number) => (state: MarketTradeSettingsStore) => {
  if (!state.settings[chainId]) {
    state.setSettings(chainId, initialSettingsForChainId(chainId));

    return {
      slippage: INITIAL_SETTINGS.slippage,
      isSlippageCustom: INITIAL_SETTINGS.isSlippageCustom,
      dynamicSlippage: INITIAL_SETTINGS.dynamicSlippage,
      setSettings: (settings: Partial<MarketTradeSettings>) => state.setSettings(chainId, settings),
    };
  }
  return {
    slippage: state.settings[chainId].slippage,
    isSlippageCustom: state.settings[chainId].isSlippageCustom,
    dynamicSlippage: state.settings[chainId].dynamicSlippage,
    setSettings: (settings: Partial<MarketTradeSettings>) => state.setSettings(chainId, settings),
  };
};

export const hasAlteredSettingsSelector = (chainId: number) => (state: MarketTradeSettingsStore) =>
  state.hasAlteredSettings(chainId);

export const recipientAddressSelector = (state: MarketTradeSettingsStore) => ({
  recipientAddress: state.recipientAddress,
  setRecipientAddress: state.setRecipientAddress,
});
